import {Commit, Dispatch, Getter} from "vuex";
import {
  ACTIVE_SHIPMENT_STATUS,
  ACTIVE_SHIPMENT_TYPE,
  CELL_SIZES,
  PAYMENT_STATUS,
  EClickCollectType,
} from "@/constants/orders";
import {Languages} from "@/constants/locale";
import {DISPLAY_MODE} from "@/constants/system";

const DEFAULT_HUB_ID = 0;
const DEFAULT_HUB_NAME = "";
const DEFAULT_HUB_DESCRIPTION = "";
const DEFAULT_HUB_ADDRESS = "";
const DEFAULT_LAT = 0;
const DEFAULT_LONG = 0;
const DEFAULT_STATUS = 0;


export interface IActionsSignature<T> {
  commit: Commit;
  dispatch: Dispatch;
  getters: {[key: string]: any};
  state: T;
}

export interface IHistoryShipment {
  id: number;
  type: ACTIVE_SHIPMENT_TYPE;
  status: ACTIVE_SHIPMENT_STATUS;
  payment_status: PAYMENT_STATUS;
  from_user_id?: number;
  to_user_id?: number;
  from_hub_id: number|null;
  to_hub_id: number|null;
  to_user_phone: string;
  from_user_phone: string;
  note: string;
  co2_saved: number;
  updated_at: string;
  created_at: string;
  recently_updated?: boolean;
}

export interface IActiveShipment extends IHistoryShipment {
  from_cell_id?: number|null;
  to_cell_id?: number|null;
}


export interface IHistoryStore {
  currentShipment: IHistoryShipment;
  shipments: IHistoryShipment[];
  page: number;
  lastEmpty: false;
}

export interface IActiveShipmentStore {
  activeShipmentsOrderedById: number[];
  activeShipments: {
   [key: string]: IActiveShipment,
  };
}

export interface ISystemStore {
  canInstall: boolean;
  isInstalled: boolean;
  displayMode: DISPLAY_MODE;
}

export interface IShipmentStore {
  // Crosstown
  to_phone_number_extension: string; // before sending to the api, this must be appended to "to_phone_number"
  to_phone_number: string; // must not have spaces
  cell_size: CELL_SIZES;
  from_hub_id: number;
  note: string;

  // click and collect
  // Click & Collect
  cc_type: EClickCollectType;
  cc_phone_number: string;
  cc_name: string;
  cc_shop_address: string;
  cc_comments: string;
  cc_hub: IHub;
}

export interface IHub {
  id: number;
  name: string;
  description: string;
  address: string;
  geo_lat: number;
  geo_long: number;
  status: number;
}

export interface IHubsStore {
  hubList: IHub[];
  favoriteHubId: number;
}

export interface IActiveOrderAwaitingAndOtherLists {
  awaitingDropoff: IActiveShipment[];
  others: IActiveShipment[];
}

export interface IUserDataStore {
  id: number;
  first_name: string;
  last_name: string;
  delivery_token: string;
  default_hub_id: number|null;
  tos_accepted: boolean|null;
  advertising_consent: boolean|null;
  nif: string;
  email: string;
  phone_number: string;
  created_at: string;
  updated_at: string;
  office_email: string;
  office_location: string;
  office_phone: string;
  one_eur_earnings: number;
  co2_saved: number;
  sms_verified: string|null;
  language: Languages;
}

export interface IVuexStore {
  shipment: IShipmentStore;
  IHubs: IHubsStore;
}

export const initialHub = (): IHub => ({
  id: DEFAULT_HUB_ID,
  name: DEFAULT_HUB_NAME,
  description: DEFAULT_HUB_DESCRIPTION,
  address: DEFAULT_HUB_ADDRESS,
  geo_lat: DEFAULT_LAT,
  geo_long: DEFAULT_LONG,
  status: DEFAULT_STATUS,
});


export const initialHistoryShipment = (): IHistoryShipment => {
  return {
    id: 0,
    type: ACTIVE_SHIPMENT_TYPE.TYPE_EXTERNAL_INCOMING,
    status: ACTIVE_SHIPMENT_STATUS.STATUS_COMPLETED,
    payment_status: PAYMENT_STATUS.PAYMENT_STATUS_DECLINED,
    from_user_id: 0,
    to_user_id: 0,
    from_hub_id: 0,
    to_hub_id: 0,
    note: '',
    co2_saved: 0,
    updated_at: '',
    created_at: '',
    recently_updated: false,
    to_user_phone: '',
    from_user_phone: '',
  };
};

export const initialShipment = (): IActiveShipment => {
  return {
    id: 0,
    type: ACTIVE_SHIPMENT_TYPE.TYPE_EXTERNAL_INCOMING,
    status: ACTIVE_SHIPMENT_STATUS.STATUS_COMPLETED,
    payment_status: PAYMENT_STATUS.PAYMENT_STATUS_DECLINED,
    from_user_id: 0,
    to_user_id: 0,
    from_cell_id: 0,
    to_cell_id: 0,
    from_hub_id: 0,
    to_hub_id: 0,
    note: '',
    co2_saved: 0,
    updated_at: '',
    created_at: '',
    from_user_phone: '',
    to_user_phone: '',
    recently_updated: false,
  };
};

export const isInitialHub = (hub: IHub): boolean => hub.id === DEFAULT_HUB_ID;

export interface IDefinePickup {
  asId: string|number;
  hubId: number;
}
